(() => {
  const money = amount => (window.luveeMoney ? window.luveeMoney(amount) : '₹' + (amount / 100).toFixed(2));
  const price = variant => variant.price - Math.floor(variant.price * variant.percent / 100);
  const safeURL = (value, image = false) => {
    try {
      const url = new URL(value, location.origin);
      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';
      if (!image && url.origin !== location.origin) return '';
      return url.href;
    } catch { return ''; }
  };
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  class LuveeSale extends HTMLElement {
    #controller;
    #timer;
    #products = [];
    #limit = 24;
    #ready = false;
    connectedCallback() {
      this.grid = this.querySelector('[data-sale-grid]');
      this.status = this.querySelector('[data-sale-status]');
      this.sort = this.querySelector('[data-sale-sort]');
      this.brand = this.querySelector('[data-sale-brand]');
      this.stock = this.querySelector('[data-sale-stock]');
      this.more = this.querySelector('[data-sale-more]');
      this.labels = JSON.parse(this.querySelector('[data-sale-labels]')?.textContent || '{}');
      this.#limit = Number(this.dataset.pageSize) || 24;
      this.sort.disabled = true;
      if (this.ended()) return this.endSale();
      this.#timer = setTimeout(() => this.endSale(), Math.min(Date.parse(this.dataset.end) - Date.now(), 2147483647));
      this.querySelector('[data-sale-controls]')?.removeAttribute('hidden');
      for (const control of [this.sort, this.brand, this.stock]) control?.addEventListener('change', () => { this.#limit = Number(this.dataset.pageSize) || 24; this.render(); });
      this.more?.addEventListener('click', () => { this.#limit += Number(this.dataset.pageSize) || 24; this.render(); });
      this.load();
    }
    disconnectedCallback() { this.#controller?.abort(); clearTimeout(this.#timer); }
    ended() { return !Number.isFinite(Date.parse(this.dataset.end)) || Date.now() >= Date.parse(this.dataset.end); }
    endSale() {
      this.#controller?.abort(); clearTimeout(this.#timer);
      for (const note of this.closest('section')?.querySelectorAll('[data-sale-promo-note]') || []) note.remove();
      this.status.textContent = this.labels.ended;
      this.sort.disabled = true;
      this.more.hidden = true;
      this.grid.replaceChildren();
      const link = node('a', 'btn', this.labels.shop_all || this.labels.ended);
      link.href = new URL('/collections/all', location.origin).href;
      this.grid.append(link);
    }
    async load() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      this.status.textContent = this.labels.loading;
      const products = new Map();
      const seen = new Set();
      let next = new URL(this.dataset.source, location.origin);
      const sourcePath = next.pathname;
      try {
        while (next) {
          if (next.origin !== location.origin || next.pathname !== sourcePath || next.searchParams.get('view') !== 'sale-data' || seen.has(next.href)) throw Error('Invalid catalogue continuation');
          seen.add(next.href);
          const response = await fetch(next, { signal: this.#controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw Error('Catalogue unavailable');
          const data = await response.json();
          if (!Array.isArray(data.products) || data.active === false) throw Error('Inactive catalogue');
          for (const product of data.products) {
            const variants = product.variants.filter(v => Number.isSafeInteger(v.price) && v.price > 0 && v.percent > 0 && v.percent <= 60 && safeURL(v.url));
            const available = variants.filter(v => v.available);
            const choices = available.length ? available : variants;
            choices.sort((a, b) => price(a) - price(b) || Number(a.id) - Number(b.id));
            // order = position in the collection, which is hand-ranked by recent demand (orders + checkouts).
            if (choices.length) products.set(String(product.id), { ...product, selected: choices[0], available: available.length > 0, order: products.size });
          }
          next = data.next ? new URL(data.next, location.origin) : null;
        }
        if (this.ended()) return this.endSale();
        this.#products = [...products.values()];
        const requested = new URL(location.href).searchParams;
        const sorts = ['trending', 'discount-desc', 'discount-asc', 'price-desc', 'price-asc'];
        if (sorts.includes(requested.get('sort'))) this.sort.value = requested.get('sort');
        for (const brand of [...new Set(this.#products.map(p => p.vendor))].sort((a,b) => a.localeCompare(b))) {
          const option = node('option', '', brand); option.value = brand; this.brand.append(option);
        }
        if ([...this.brand.options].some(o => o.value === requested.get('brand'))) this.brand.value = requested.get('brand');
        this.#ready = true;
        this.sort.disabled = false;
        this.querySelector('[data-sale-pagination]')?.remove();
        this.render();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = this.labels.error;
        this.sort.disabled = true;
        this.more.hidden = true;
        const retry = node('button', 'btn btn--outline', this.labels.retry || 'Retry');
        retry.type = 'button';
        retry.addEventListener('click', () => { retry.remove(); this.load(); }, { once: true });
        this.status.append(' ', retry);
      }
    }
    render() {
      if (this.ended()) return this.endSale();
      if (!this.#ready) return;
      const list = this.#products.filter(p => (!this.brand.value || p.vendor === this.brand.value) && (!this.stock.checked || p.available));
      const kind = this.sort.value;
      list.sort((a,b) => {
        // Trending: in-stock first, then the collection's demand ranking.
        if (kind === 'trending') return (b.available - a.available) || a.order - b.order;
        const difference = kind.startsWith('price') ? price(a.selected) - price(b.selected) : a.selected.percent - b.selected.percent;
        const primary = kind.endsWith('desc') ? -difference : difference;
        return primary || a.title.localeCompare(b.title) || Number(a.id) - Number(b.id);
      });
      const fragment = document.createDocumentFragment();
      for (const product of list.slice(0, this.#limit)) fragment.append(this.card(product));
      this.grid.replaceChildren(fragment);
      this.status.textContent = `${list.length} ${this.labels.count}`;
      if (!list.length) this.grid.append(node('p', 'muted', this.labels.empty));
      this.more.hidden = this.#limit >= list.length;
      const url = new URL(location.href);
      url.searchParams.set('sort', kind);
      if (this.brand.value) url.searchParams.set('brand', this.brand.value); else url.searchParams.delete('brand');
      url.searchParams.delete('page');
      history.replaceState(null, '', url);
    }
    card(product) {
      const variant = product.selected;
      const card = node('article', 'card');
      card.dataset.saleProduct = product.id;
      const wish = node('button', 'card__wish'); wish.type = 'button';
      wish.dataset.wishToggle = ''; wish.dataset.h = product.handle; wish.dataset.t = product.title;
      wish.dataset.u = safeURL(variant.url); wish.dataset.p = money(price(variant)); wish.dataset.i = product.image || '';
      wish.setAttribute('aria-label', `${this.labels.save || 'Save'} ${product.title}`);
      const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); icon.setAttribute('viewBox', '0 0 24 24'); icon.setAttribute('aria-hidden', 'true');
      const heart = document.createElementNS('http://www.w3.org/2000/svg', 'path'); heart.setAttribute('d', 'M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21.2l7.7-7.7 1.1-1.1a5.5 5.5 0 0 0 0-7.8z');
      icon.append(heart); wish.append(icon); card.append(wish);
      const media = node('a', 'card__media'); media.href = safeURL(variant.url); media.setAttribute('aria-label', product.title);
      media.append(node('span', 'card__badge', `${variant.percent}% ${this.labels.off || 'OFF'}`));
      const imageURL = product.image && safeURL(product.image, true);
      if (imageURL) {
        const img = node('img', 'card__img'); img.src = imageURL; img.alt = product.title; img.loading = 'lazy';
        img.width = product.imageWidth || 460; img.height = product.imageHeight || 460; media.append(img);
      }
      const body = node('div', 'card__body');
      body.append(node('span', 'card__brand', product.vendor));
      const title = node('a', 'card__title', product.title); title.href = media.href; body.append(title);
      if (variant.title !== 'Default Title') body.append(node('span', 'festival-sale__pack', variant.title));
      const costs = node('div', 'card__price');
      const payable = money(price(variant));
      costs.setAttribute('aria-label', `${this.labels.sale_price} ${payable}. ${this.labels.regular_price} ${money(variant.price)}.`);
      costs.append(node('span', '', payable), node('s', '', money(variant.price))); body.append(costs);
      const add = node('button', 'card__add', product.available ? this.labels.add : this.labels.sold); add.type = 'button';
      if (product.available) add.dataset.add = variant.id; else add.disabled = true;
      body.append(add); card.append(media, body); return card;
    }
  }
  if (!customElements.get('luvee-sale')) customElements.define('luvee-sale', LuveeSale);
})();
